import React, { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';
import classNames from 'classnames';
import cx from './index.module.scss';
import { componentSizeVariantsEnum } from 'src/constants/common.constants';
import Label from '../formFields/label/Label';
import ErrorMessage from '../formFields/errorMessage/ErrorMessage';

type TInputFieldProps = {
    errorMessage?: string;
    ref?: any;
    label?: ReactNode;
    id: string;
    variant?: componentSizeVariantsEnum;
    startIcon?: React.ReactNode;
    removable?: boolean;
    onRemove?: () => void;
    endIcon?: React.ReactNode;
    startIconClickHandler?: () => void;
    endIconClickHandler?: () => void;
    maskField?: boolean;
    elementtype?: string;
    inputFieldClassName?: string;
    isTouched?: boolean;
    inputRef?: any;
    required?: boolean;
    component?: React.ReactNode,
    onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
} & InputHTMLAttributes<HTMLInputElement>;

/**
 * Props for the InputField component.
 * @typedef {Object} TInputFieldProps
 * @param {string} [errorMessage] - The error message to be displayed.
 * @param {string} label - The label for the input field.
 * @param {string} [id] - The ID attribute for the input field.
 * @param {componentSizeVariantsEnum} [variant] - The variant of the input field.
 * @param {React.ReactNode} [startIcon] - The icon element to be displayed at the start.
 * @param {React.ReactNode;} [endIcon] - The icon element to be displayed at the end.
 * @param {string} [inputFieldClassName] - Additional class name(s) for the input field.
 * @param {() = void} [startIconClickHandler] - Click handler for the left icon.
 * @param {() = void} [endIconClickHandler] - Click handler for the right icon.
 * @param {boolean} [markField] - The icon element to be displayed at the end.
 * @param {boolean} [isTouched] - Whether the field is touched.
 * @param {InputHTMLAttributes<HTMLInputElement>} [HTMLInputElement] - HTML attributes of the input element.
 */
const ExpandableInputField = ({
    errorMessage,
    label,
    variant = componentSizeVariantsEnum.MEDIUM,
    startIcon,
    endIcon,
    inputRef,
    component = <></>,
    placeholder,
    className,
    isTouched,
    required = false,
    value,
    removable = false,
    onRemove = () => { },
    onChange
}: TInputFieldProps) => {
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange && onChange(e);
    };

    return (
        <div
            className={classNames(
                className,
                'input-field-component',
                `input-field-component--${variant}`
            )}
        >
            {label && (
                <div className='input-field-component__labelBox'>
                    <Label variant={variant} required={required}>
                        <span>{label}</span>
                    </Label>
                    {component}
                    {removable && <span className='input-field-component__labelBox__remove-button' onClick={() => onRemove && onRemove()}>Remove</span>}
                </div>
            )}
            <textarea
                ref={inputRef}
                value={value}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                    handleChange && handleChange(e)
                }}
                placeholder={placeholder}
                className={cx.input}
                style={{ overflow: 'hidden', resize: 'none', background: 'transparent' }}
            />
            {isTouched && errorMessage && <ErrorMessage message={errorMessage} />}
        </div >
    );
};

export default ExpandableInputField;
